import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, GoogleLogo, ChartLine, Lightning, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        navigate("/dashboard", { replace: true });
      } catch {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500">Checking session…</div>;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f4f4f0]">
      {/* Left – brand + copy */}
      <div className="relative p-10 lg:p-14 flex flex-col justify-between grid-bg">
        <div className="brand-mark text-2xl" data-testid="brand-mark">
          <img src="/brand/logo-orangeburst.webp" alt="QuickSuite" />
          <span className="divider" />
          <span className="sub">Connect</span>
        </div>

        <div className="max-w-lg space-y-6">
          <div className="inline-flex items-center gap-2 border border-neutral-300 bg-white px-3 py-1 text-xs uppercase tracking-widest">
            <span className="w-1.5 h-1.5 bg-[#F58021]" /> Sheets → Reviews, automated
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
            Every new row.<br />
            An instant review request.
          </h1>
          <p className="text-neutral-600 text-lg leading-relaxed">
            Connect a Google Sheet. When your team drops a customer in — name, email, phone — we
            fire off a review invite through your MySaaS workflow. No copy&#8209;paste. No delay.
          </p>

          <div className="grid gap-3 pt-4">
            <FeatureRow icon={<Lightning weight="duotone" size={20} />} title="Real-time polling" desc="New rows detected every 60 seconds." />
            <FeatureRow icon={<ChartLine weight="duotone" size={20} />} title="Full request history" desc="Payload + response for every invite. Retry any failure." />
            <FeatureRow icon={<ShieldCheck weight="duotone" size={20} />} title="Your keys, isolated" desc="Each connection uses your own MySaaS API key + location." />
          </div>
        </div>

        <div className="text-xs text-neutral-500">© {new Date().getFullYear()} QuickSuite Connect</div>
      </div>

      {/* Right – auth panel */}
      <div className="relative flex items-center justify-center p-8 bg-[#0A0A0A] text-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">01 &nbsp;/&nbsp; Sign in</div>
            <h2 className="font-display text-4xl font-black tracking-tight">Get started</h2>
            <p className="text-neutral-400 mt-3">Sign in with your Google account. We only use it to authenticate you — sheet access is granted separately, per connection.</p>
          </div>

          <Button
            data-testid="google-signin-btn"
            onClick={handleLogin}
            className="w-full h-14 bg-white text-black hover:bg-neutral-200 rounded-md text-base font-semibold gap-3"
          >
            <GoogleLogo weight="bold" size={22} />
            Continue with Google
            <ArrowRight weight="bold" size={18} className="ml-auto" />
          </Button>

          <div className="pt-8 border-t border-neutral-800 text-xs text-neutral-500 leading-relaxed">
            By continuing you agree to our terms. You can revoke the QuickSuite Connect service account access at any time from Settings.
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 border border-neutral-200 bg-white/70 backdrop-blur-sm px-4 py-3">
      <div className="text-[#F58021] mt-0.5">{icon}</div>
      <div>
        <div className="font-semibold text-sm text-neutral-900">{title}</div>
        <div className="text-neutral-500 text-sm">{desc}</div>
      </div>
    </div>
  );
}
